/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import Highcharts from 'highcharts-pack/highcharts/highcharts';
import { IndividualSeriesOptions } from 'highcharts';
import datasetTypes from '../../enums/datasetTypes';
import PieSeriesGenericChartVisualization from './chart/PieSeriesGenericChartVisualization';
import genericChartVisualizationStaticPropertiesReducer from './chart/reducers/genericChartVisualizationReducer';
import {
    getMeasures,
    findLayoutItemByIndex,
    findLayoutItemById,
    getFieldToLabelMeasureMapping,
    getIdToLabelMap
} from './chart/util/GenericSeriesUtil';
import i18n from '../../../i18n/AdHocBundle.properties';
import { getEvents, getOutputParamsKeyFromField, getOutputParamFromMeasure } from './chart/util/EventUtil';
import formattersFactory from '../../formatters/formattersFactory';

// Get the series fo a dual level pie, taking from the first level the series of the inner pie and from
// every child the series of the outer pie. Proportional size of every chart is fixed

export class DualLevelPieVisualization extends PieSeriesGenericChartVisualization {

    useShadow = false

    getSeriesSection(selectedColIndex: number, selectedRowIndex: number): IndividualSeriesOptions[] {
        const dataset = this.getDataset();
        const linkOptions = this.getLinkOptions();
        const staticProperties = this.getStaticProperties();
        const colors = this.getColorsToUse();
        const { levelsStructure } = staticProperties;
        const idToLabelMap = getIdToLabelMap(staticProperties);
        const measures = getMeasures(staticProperties.levelsStructure);
        const measureFieldToLabelMapping = getFieldToLabelMeasureMapping(levelsStructure);
        const measuresFromRows :any = staticProperties.levelsStructure.rows.find((level: any) => (level.kind === 'measures'));
        const measuresLocatedOnColumns :boolean = Boolean(!measuresFromRows);

        let oneMeasureInRows = false;
        if (measuresFromRows) {
            oneMeasureInRows = measuresFromRows.measures.length === 1;
        }

        const columnSeries = this.getColumnSeries();

        // this map is used to cache some calls to function which finds element by its index
        const columnIndexToLayoutItemMap :any = {};
        const rowLabelToLayoutItemMap :any = {};
        const rowChildLabelToLayoutItemMap :any = {};

        // check out what is the first item in Rows
        const firstRowItemIsField = dataset.summary.rows.items[0].field !== undefined;
        let amountOfFieldsInRows = 0;
        dataset.summary.rows.items.forEach((item: any) => {
            if (item.field) {
                amountOfFieldsInRows += 1;
            }
        });
        // Rows should have at least one field to render chart, so, if Rows consist of one item, then it's a field
        const onlyOneFieldInRows = dataset.summary.rows.items.length === 1;

        // Below you may find all combinations for the Dual Level chart Layout and the tooltip values.
        // Also, in this description I'm trying to depict the inner and outer slices of the double-level pie chart as
        //    InnerTooltip ) OuterTooltip
        // where ')' - is a separator between inner and outer slices.
        //
        // In the description below I'm also using the tooltip format as:
        //                      NameFromDataArrayItemFromInnerSeries ) NameFromDataArrayItemFromOuterSeries
        //   InnerSeriesName : ValueFromDataArrayItemFromInnerSeries ) OuterSeriesName : ValueFromDataArrayItemFromOuterSeries
        //
        // Also, here are some abbreviations:
        //    M, M1 and M2 - are some Measure
        //    F, F1 and F2 - are some Fields
        //    Totals - is just a text "Totals"
        //    Values - is just a text "Values"
        //
        // So, there are 6 combinations for the Dual Level Pie chart which are:
        //
        // 1)
        // Columns:         M  ==>     Totals ) F
        // Rows:            F  ==>       M: X ) M: X
        //
        // 2)
        // Columns:         M  ==>         F1 ) F1, F2
        // Rows:       F1, F2  ==>       M: X ) M: X
        //
        // 3)
        // Columns:            ==>          M ) F, M
        // Rows:         M, F  ==>  Totals: X ) Value: X
        //
        // 4)
        // Columns:            ==>          F ) M, F
        // Rows:         F, M  ==>   Value: X ) Value: X
        //
        // 5)
        // Columns:            ==>  {M1 | M2} ) F, {M1 | M2}
        // Rows:    M1, M2, F  ==>  Totals: X ) Value: X
        //
        // 6)
        // Columns:            ==>          F ) {M1 | M2}, F
        // Rows:    F, M1, M2  ==>  Totals: X ) Value: X

        let firstMinMaxRecorded :boolean = false;
        let min :number = 0,
            max :number = 0;
        const finalSeries: any[] = [];

        columnSeries.forEach((seriesObj: any) => {
            const { series, colIndex } = seriesObj;

            if (typeof columnIndexToLayoutItemMap[colIndex] === 'undefined') {
                columnIndexToLayoutItemMap[colIndex] = findLayoutItemByIndex(colIndex, dataset.data, levelsStructure);
            }
            if (!columnIndexToLayoutItemMap[colIndex]) {
                // if Rows contains one Measure followed by one Field then the Measure has index -1
                columnIndexToLayoutItemMap[colIndex] = findLayoutItemByIndex(-1, dataset.data, levelsStructure);
            }
            const columnLayoutItem :any = columnIndexToLayoutItemMap[colIndex];

            /* eslint-disable camelcase */
            const innerData: { name: any, y: any, color: string, linkOutputParams: any, jrsTooltipValue? :any }[] = [];
            const outerData: { name: any, y: any, color: string, linkOutputParams: any, jrsTooltipValue? :any }[] = [];
            /* eslint-enable camelcase */

            if (onlyOneFieldInRows) {

                // this code is for case number 1 only

                dataset.data.rows.children.forEach((row: any, index: number) => {
                    const value = +(dataset.data.aggregations[row.index][colIndex]);
                    const nameValue = `${row.label}`;
                    const brightness = 0.2 - (index / dataset.data.rows.children.length) / 5;
                    const rowChildColor = Highcharts.Color(colors[0]).brighten(brightness).get();
                    const outerLinkOutputParams: any = {
                        measures: [getOutputParamFromMeasure(measures[0])],
                        values: [
                            {
                                key: getOutputParamsKeyFromField(dataset.summary.rows.items[0]),
                                value: row.label
                            }
                        ]
                    }

                    let tooltipValue = formattersFactory(this.defaultFieldAggregationType).format(value.toString(), this.defaultFieldAggregationFormat);
                    if (columnLayoutItem) {
                        tooltipValue = formattersFactory(columnLayoutItem.aggregationType).format(value.toString(), columnLayoutItem.aggregationFormat);
                    }

                    outerData.push({
                        name: nameValue,
                        y: value,
                        jrsTooltipValue: tooltipValue,
                        color: rowChildColor,
                        linkOutputParams: outerLinkOutputParams
                    });
                    if (!firstMinMaxRecorded) {
                        min = value;
                        max = value;
                        firstMinMaxRecorded = true;
                    } else {
                        min = Math.min(value, min);
                        max = Math.max(value, max);
                    }
                });

                const innerLinkOutputParams: any = {
                    measures: [getOutputParamFromMeasure(measures[0])],
                    values: [
                        {
                            key: getOutputParamsKeyFromField(dataset.summary.rows.items[0]),
                            value: 'total_node'
                        }
                    ]
                }
                const innerTotal = +(dataset.data.aggregations[dataset.data.rows.total!.index][colIndex]);

                let tooltipValue = formattersFactory(this.defaultFieldAggregationType).format(innerTotal.toString(), this.defaultFieldAggregationFormat);
                if (columnLayoutItem) {
                    tooltipValue = formattersFactory(columnLayoutItem.aggregationType).format(innerTotal.toString(), columnLayoutItem.format);
                }

                innerData.push({
                    name: i18n['adhoc.node.total.node'],
                    y: innerTotal,
                    jrsTooltipValue: tooltipValue,
                    color: colors[0],
                    linkOutputParams: innerLinkOutputParams
                });

            } else {

                // this code is for cases numbered 2 - 6

                dataset.data.rows.children.forEach((rowItemLevel1: any, rowIndexLevel1: number) => {

                    if (typeof rowLabelToLayoutItemMap[rowItemLevel1.label] === 'undefined') {
                        rowLabelToLayoutItemMap[rowItemLevel1.label] = findLayoutItemById(rowItemLevel1.label, levelsStructure);
                    }
                    const rowLayoutItem :any = rowLabelToLayoutItemMap[rowItemLevel1.label];

                    let innerTotal = 0;
                    const { isMeasure } = rowItemLevel1;

                    rowItemLevel1.children.forEach((rowItemLevel2: any, rowIndexLevel2: number) => {

                        if (typeof rowChildLabelToLayoutItemMap[rowItemLevel2.label] === 'undefined') {
                            rowChildLabelToLayoutItemMap[rowItemLevel2.label] = findLayoutItemById(rowItemLevel2.label, levelsStructure);
                        }
                        const rowChildLayoutItem :any = rowChildLabelToLayoutItemMap[rowItemLevel2.label];

                        let name :any = [];
                        let rowChildLabel = measureFieldToLabelMapping[rowItemLevel2.label] || rowItemLevel2.label;
                        if (rowItemLevel2.isMeasure && !this.showMeasureName()) {
                            rowChildLabel = '';
                        }
                        if (rowChildLabel) {
                            name.push(rowChildLabel);
                        }
                        let measureOrFieldLabel = idToLabelMap.has(rowItemLevel1.label) ? idToLabelMap.get(rowItemLevel1.label) : rowItemLevel1.label;
                        if (isMeasure && !this.showMeasureName()) {
                            measureOrFieldLabel = '';
                        }
                        if (measureOrFieldLabel) {
                            name.push(measureOrFieldLabel);
                        }
                        name = name.reverse().join(', ');

                        const brightness = 0.2 - (rowIndexLevel2 / rowItemLevel1.children.length) / 5;
                        const rowChildColor = Highcharts.Color(colors[rowIndexLevel1 % colors.length] || colors[0]).brighten(brightness).get();
                        const outerLinkOutputParams: any = {
                            measures: [],
                            values: []
                        };

                        if (amountOfFieldsInRows === 2) {
                            // this is case number 2: we have two fields in rows, and one measure in column
                            outerLinkOutputParams.measures.push(getOutputParamFromMeasure(measures[0]));
                            outerLinkOutputParams.values.push(
                                {
                                    key: getOutputParamsKeyFromField(dataset.summary.rows.items[0]),
                                    value: rowItemLevel1.label
                                }
                            );
                            outerLinkOutputParams.values.push(
                                {
                                    key: getOutputParamsKeyFromField(dataset.summary.rows.items[1]),
                                    value: rowItemLevel2.label
                                }
                            );
                        } else if (firstRowItemIsField) {
                            // this is case number 4 or 6: we have one field in rows followed by one or two measures
                            outerLinkOutputParams.measures.push(getOutputParamFromMeasure(measures[rowIndexLevel2]));
                            outerLinkOutputParams.values.push(
                                {
                                    key: getOutputParamsKeyFromField(dataset.summary.rows.items[0]),
                                    value: rowItemLevel1.label
                                }
                            );
                        } else {
                            // this is case number 3 or 5: we have one or two measures followed by one field in rows
                            outerLinkOutputParams.measures.push(getOutputParamFromMeasure(measures[rowIndexLevel1]));
                            outerLinkOutputParams.values.push(
                                {
                                    key: getOutputParamsKeyFromField(dataset.summary.rows.items[dataset.summary.rows.items.length - 1]),
                                    value: rowItemLevel2.label
                                }
                            );
                        }

                        const value = +(dataset.data.aggregations[rowItemLevel2.index][colIndex]);
                        innerTotal += value;

                        let tooltipValue = formattersFactory(this.defaultFieldAggregationType).format(value.toString(), this.defaultFieldAggregationFormat);
                        const layoutItem = rowChildLayoutItem || rowLayoutItem || columnLayoutItem;
                        if (layoutItem) {
                            tooltipValue = formattersFactory(layoutItem.aggregationType).format(value.toString(), layoutItem.aggregationFormat);
                        }

                        outerData.push({
                            name,
                            y: value,
                            jrsTooltipValue: tooltipValue,
                            color: rowChildColor,
                            linkOutputParams: outerLinkOutputParams
                        });
                        if (!firstMinMaxRecorded) {
                            min = value;
                            max = value;
                            firstMinMaxRecorded = true;
                        } else {
                            min = Math.min(value, min);
                            max = Math.max(value, max);
                        }
                    });

                    let innerLinkOutputParams: any;
                    if (firstRowItemIsField) {
                        innerLinkOutputParams = {
                            measures: [getOutputParamFromMeasure(measures[0])],
                            values: [
                                {
                                    key: getOutputParamsKeyFromField(dataset.summary.rows.items[0]),
                                    value: rowItemLevel1.label
                                }
                            ]
                        }
                    } else {
                        innerLinkOutputParams = {
                            measures: [getOutputParamFromMeasure(measures[rowIndexLevel1])],
                            values: []
                        };
                    }

                    let name = rowItemLevel1.label;
                    if (isMeasure && !this.showMeasureName()) {
                        name = '';
                    }
                    if (idToLabelMap.has(name)) {
                        name = idToLabelMap.get(name);
                    }

                    let tooltipValue = formattersFactory(this.defaultFieldAggregationType).format(innerTotal.toString(), this.defaultFieldAggregationFormat);
                    const layoutItem = rowLayoutItem || columnLayoutItem;
                    if (layoutItem) {
                        tooltipValue = formattersFactory(layoutItem.aggregationType).format(innerTotal.toString(), layoutItem.aggregationFormat);
                    }

                    innerData.push({
                        name,
                        y: innerTotal,
                        jrsTooltipValue: tooltipValue,
                        color: colors[rowIndexLevel1 % colors.length],
                        linkOutputParams: innerLinkOutputParams
                    });
                });
            }

            let innerSeriesName = series.name;
            let outerSeriesName = series.name;
            if (!measuresLocatedOnColumns) {
                // these are cases from 3 to 6
                outerSeriesName = i18n['adhoc.tooltip.value'];
                innerSeriesName = i18n['adhoc.node.total.node'];
                if (firstRowItemIsField && oneMeasureInRows) {
                    // this is case number 4
                    innerSeriesName = i18n['adhoc.tooltip.value'];
                }
            }

            finalSeries.push({
                name: innerSeriesName,
                data: innerData,
                showInLegend: false,
                center: ['50%', '50%'],
                size: '60%',
                dataLabels: {
                    color: 'white',
                    distance: -50,
                    style: {
                        fontSize: '1em'
                    }
                },
                point: {
                    events: getEvents(linkOptions)
                }
            });

            finalSeries.push({
                name: outerSeriesName,
                data: outerData,
                showInLegend: true,
                center: ['50%', '50%'],
                size: '90%',
                innerSize: '60%',
                dataLabels: {
                    enabled: false,
                    style: {
                        fontSize: '1em'
                    }
                },
                point: {
                    events: getEvents(linkOptions)
                }
            });
        });

        this.recordMinMaxValues(min, max);

        return finalSeries;
    }

    getPlotOptionsSection(): any {
        const plotOptions = super.getPlotOptionsSection();
        plotOptions.pie.point.events = {
            legendItemClick(event: any) {
                event.preventDefault();
            }
        };
        return plotOptions;
    }

    getChartSection() {
        const chart = super.getChartSection();

        return {
            ...chart,
            type: 'pie'
        };
    }

    render() {
        const props = this.getProps();
        const {
            handle,
            dataset: {
                summary: {
                    rows: {
                        items
                    }
                }
            }
        } = props;

        if (items[1] && !items[0].measuresLevel && !items[1].measuresLevel && !items[1].expanded) {
            handle.updateLevelExpansion(items[1].id, true);
        }

        return super.render();
    }

}

export const DualLevelPieVisualizationDescriptor = {
    legacyAdhoc: 'dual_level_pie',
    cssClass: 'DualLevelPie',
    bundleName: 'adhoc.visualisation.chooser.type.DUAL_LEVEL_PIE',
    name: 'DualLevelPie',
    group: 'adhoc.visualisation.chooser.type.GROUP_PIE',
    datasetType: datasetTypes.MULTI_AXIS,
    component: DualLevelPieVisualization,
    staticPropertiesReducer: genericChartVisualizationStaticPropertiesReducer,
    supportsResize: false,
    requirements: {
        measures: { min: 1 },
        fields: {
            min: 1,
            inColumn: false
        },
        placement: {
            allowed: [
                {
                    column: 'm',
                    row: '[f]{1,2}'
                },
                {
                    column: '',
                    row: 'f[m]+'
                },
                {
                    column: '',
                    row: '[m]+f'
                }
            ]
        }
    }
};
